import { RouterProvider, createBrowserRouter } from "react-router";

import TaskPage from "./pages/TaskPage";
import DevelopmentPlan from "./pages/DevelopmentPlan/DevelopmentPlan";
import { App } from "./App";
import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect, useState } from "react";
import {CONFIG} from "./constants";
import AcceptInvite from "./pages/AcceptInvitePage";
import SupportPage from "./pages/SupportPage";
import AboutPage from "./pages/AboutPage";
import Api from "./services/Api";
import VideocoachPage from "./pages/VideocoachPage";
import CoachChat from "./pages/VideocoachPage/CoachChat"
import BookMeetingPage from "./pages/BookMeetingPage";
import MatchWithCoachPage from "./pages/MatchWithCoachPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import MyCoachPage from "./pages/VideocoachPage/MyCoachPage";
import Unsubscribe from "./pages/Unsubscribe/Unsubscribe";
import TaskPageEdit from "./pages/TaskPage/TaskPageEdit";
import AiStart from "./pages/AiThreads/AiStart";
import Home from "./pages/Home/Home";
import ProfileMenu from "./pages/Profile/ProfileMenu";
import BigFive from "./pages/Profile/BigFive/BigFive";
import StressTest from "./pages/Profile/StressTest";
import SurveyResponse from "./pages/FeedbackRespondentPage/SurveyResponse";
import SurveyMultiResponse from "./pages/FeedbackRespondentPage/SurveyMultiResponse";
import SurveyResult from "./pages/SurveyResult/SurveyResult";
import SurveyRequestInvite from "./pages/SurveyResult/SurveyRequestInvite";
import AiSystem from "./pages/AiThreads/AiSystem";
import Testing from "./pages/Testing/Testing";
import Team from "./pages/Team/Team";
import TeamMember from "./pages/Team/TeamMember";
import Assessments from "./pages/Assessments/Assessments";
import TeamVisionStatement from "./pages/Team/TeamVisionStatement";

import CoachHome from "./coachPages/Home/CoachHome";
import CoachAccount from "./coachPages/Account/CoachAccount";
import CoachBookings from "./coachPages/Bookings/CoachBookings";
import ClientsPage from "./coachPages/Clients/ClientsPage";
import ClientPage from "./coachPages/Clients/ClientPage";
import NotFound from "./pages/NotFound";
import ClientChatPage from "./coachPages/Clients/ClientChatPage";
import ChatsPage from "./coachPages/Chats/ChatsPage";
import CoachSupportPage from "./coachPages/SupportPage";
import Availability from "./coachPages/Availability/Availability"
import CoachVisionStatement from "./coachPages/VisionStatement/CoachVisionStatement";
import SignUp from "./coachPages/SignUp/SignUp";
import ApplicationPage from "./coachPages/Application/Application";
import LongTermGoal from "./pages/DevelopmentPlan/LongTermGoal";
import TeamRecordings from "./pages/Team/TeamRecordings";
import TeamRecording from "./pages/Team/TeamRecording";
import SelfAssessmentSurveyPage from "./pages/FeedbackRespondentPage/SelfAssessmentSurveyPage";
import MeetingSummary from "./coachPages/Clients/MeetingSummary";
import Readiness from "./pages/Readiness/Readiness";
import Drivers from "./pages/Drivers/Drivers";
import AssessmentUpload from "./pages/Assessments/AssessmentUpload";
import CustomerSurveyReport from "./pages/Assessments/CustomerSurveyReport";

interface Props {
  outerRoute: string|undefined|null
}

const AppRouter: React.FC<Props> = ({ outerRoute }) => {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();
  // Initialize api here with the getAccessTokenSilently function. This is need to allow api to get/refresh token
  let api = Api.getInstance(getAccessTokenSilently);
  const [enoughPrivileges, setEnoughPrivileges] = useState(false);
  const [error, setError] = useState("");

  // Define routes without handle properties
  const clientRoutes = [
    {
      path: '/',
      element: <Home />,
    },
    {
      path: '/ai/:urlThreadId',
      element: <AiStart />,
    },
    {
      path: '/ai_system',
      element: <AiSystem />,
    },
    {
      path: '/tasks/:taskId',
      element: <TaskPage />,
    },
    {
      path: '/tasks/:taskId/edit',
      element: <TaskPageEdit />,
    },
    {
      path: '/accept_invite/:code',
      element: <AcceptInvite />,
    },
    {
      path: '/assessments',
      element: <Assessments />,
    },
    {
      path: '/assessments/own/:id',
      element: <SelfAssessmentSurveyPage/>,
    },
    {
      path: '/assessments/survey_upload/:id',
      element: <AssessmentUpload/>,
    },
    {
      path: '/assessments/customer_survey_report/:id',
      element: <CustomerSurveyReport/>,
    },
    {
      path: '/dev-plan',
      element: <DevelopmentPlan />,
    },
    {
      path: '/assessments/readiness/:categoryId',
      element: <Readiness />,
    },
    {
      path: '/dev-plan/survey_request_invite/:id',
      element: <SurveyRequestInvite />,
    },
    {
      path: '/dev-plan/long-term-goal/:visionStatementId',
      element: <LongTermGoal/>
    },
    {
      path: '/dev-plan/survey/result/:id/:page',
      element: <SurveyResult />,
    },
    {
      path: '/feedback/survey/:id',
      element: <SurveyResponse />,
    },
    {
      path: '/feedback/survey_multi/:survey_multi_recipient_id',
      element: <SurveyMultiResponse />,
    },
    {
      path: '/feedback/survey_drivers/:customerCategoryId',
      element: <Drivers />,
    },
    {
      path: '/account',
      element: <ProfilePage />,
    },
    {
      path: '/about',
      element: <AboutPage />,
    },
    {
      path: '/support',
      element: <SupportPage />,
    },
    {
      path: '/team',
      element: <Team />,
    },
    {
      path: '/team/member/:clientId',
      element: <TeamMember />
    },
    {
      path: '/team/vision_statement/:visionStatementId',
      element: <TeamVisionStatement />
    },
    {
      path: '/team/recordings',
      element: <TeamRecordings />
    },
    {
      path: '/team/recording/:recordingId',
      element: <TeamRecording />
    },
    // Videocoach
    {
      path: '/videocoach',
      element: <VideocoachPage />,
    },
    {
      path: '/videocoach/my-coach',
      element: <MyCoachPage />,
    },
    {
      path: '/videocoach/chat/:coachId',
      element: <CoachChat />,
    },
    {
      path: '/videocoach/book-meeting',
      element: <BookMeetingPage />,
    },
    {
      path: '/videocoach/match-with-coach',
      element: <MatchWithCoachPage />,
    },
    {
      path: '/unsubscribe',
      element: <Unsubscribe />
    },
    {
      path: '/profile',
      element: <ProfileMenu />
    },
    {
      path: '/profile/big5',
      element: <BigFive />
    },
    {
      path: '/profile/stress',
      element: <StressTest />
    },
    {
      path: '/testing',
      element: <Testing />
    },
    {
      path: '/signup',
      element: <SignUp />,
    },
    {
      path: '*',
      element: <NotFound/>
    },
  ];

  const coachRoutes = [
    {
      path: '/',
      element: <CoachHome />,
    },
    {
      path: '/signup',
      element: <SignUp />,
    },
    {
      path: '/account',
      element: <CoachAccount />,
    },
    {
      path: '/application',
      element: <ApplicationPage />,
    },
    {
      path: '/bookings',
      element: <CoachBookings />,
    },
    {
      path: '/clients',
      element: <ClientsPage />,
    },
    {
      path: '/clients/:clientId',
      element: <ClientPage />
    },
    {
      path: '/clients/vision_statement/:id',
      element: <CoachVisionStatement />,
    },
    {
      path: '/clients/long_term_goal/:visionStatementId',
      element: <LongTermGoal />,
    },
    {
      path: '/clients/:clientId/meeting_summary/:meetingId',
      element: <MeetingSummary />
    },
    {
      path: '/chats/:clientId',
      element: <ClientChatPage />
    },
    {
      path: '/clients/stress/:stressId',
      element: <StressTest/>
    },
    {
      path: '/clients/personality/:personalityId',
      element: <BigFive/>
    },
    {
      path: '/clients/survey_result/:id/:page',
      element: <SurveyResult />,
    },
    {
      path: '/chats',
      element: <ChatsPage />
    },
    {
      path: '/support',
      element: <CoachSupportPage />
    },
    {
      path: '/availability',
      element: <Availability />
    },
    {
      path: '*',
      element: <NotFound/>
    }
  ];

  const router = createBrowserRouter([
    {
      path: "/",
      element: <App />,
      children: CONFIG.USER_TYPE === "client" ? clientRoutes : coachRoutes,
    },
  ]);

  useEffect(() => {
    const pathname = window.location.pathname

    // If we get here from an access request redirect, capture code and store in local storage
    if (pathname.startsWith('/availability') && window.location.search) {
      if (!window.localStorage["oauth_temp"]) {
        window.localStorage["oauth_temp"] = window.location.search
      } else {
        console.log('found oauth_temp')
      }
    }
  }, []);

  useEffect(() => {
    if (outerRoute) {
      router.navigate(outerRoute).then();
    }
  }, [outerRoute]);

  // console.log('isLoading: ' + isLoading + ' isAuthenticated: ' + isAuthenticated);

  useEffect(() => {
    if ((window.location.pathname.startsWith('/feedback/survey') && window.location.search.startsWith('?token')) ||
      window.location.pathname === "/signup") {
      // When there is no need for a logged-in session, return
      setEnoughPrivileges(true)
      return
    }
    if (!isLoading) {
      if (!isAuthenticated) {
        const loginOptions = {
          appState: {
            returnTo: window.location.pathname,
          },
          authorizationParams: {}
        };

        if (window.location.pathname.startsWith('/accept_invite/')) {
          loginOptions.authorizationParams = {screen_hint: "signup"};
        }

        loginWithRedirect(loginOptions).then();

      } else {
        if (window.location.pathname.startsWith('/accept_invite/')) {
          console.log('Accept invite, authenticated');
          const invite_code = window.location.pathname.substring(15);
          const data = {
            code: invite_code
          }
          const url = CONFIG.USER_TYPE === "client" ? "client/accept_invite" : "coach/accept_invite";
          api.post(url, data).then(response => {
            if (response.json.status === "ok") {
              setEnoughPrivileges(true);
              window.location.href = "/";
            } else if (response.json.error) {
              setError(response.json.error)
            } else {
              setError('Unknown error. Please contact support')
            }
          });
        } else {
          // There is an unknown problem of getAccessTokenSilently failing to get refresh token. Try this method
          // here, and if it fails, run the loginWithRedirect method to get a new refresh token.
          getAccessTokenSilently().then((accessToken: string) => {
            console.log("Got access token, length " + accessToken.length);
            setEnoughPrivileges(true);
          }).catch((reason: any) => {
            console.log(reason);
            const loginOptions = {
              appState: {
                returnTo: window.location.pathname,
              }
            };
            loginWithRedirect(loginOptions).then();
          })
        }
      }
    }
  }, [isLoading, isAuthenticated]);

  if (error.length > 0) {
    return (<div className="mt-4 text-center text-gray-100">{error}</div>);
  }
  if (!enoughPrivileges) {
    return (<div className="mt-4 text-center text-indigo-800">Loading...</div>)
  }

  return <RouterProvider router={router} />;
};

export default AppRouter;
